import {
  BrowserMessageType,
  exchangeElectronMessage,
} from 'electron-communication-lib/BrowserForApp';

import handledBackend, {
  LockedDocumentInResponse,
} from '@lexian/api/documents-api';
import {isElectron} from '_/utilities';

export type DialogType =
  | 'IN_AN_ACTION'
  | 'LAST_LOGIN'
  | 'FROM_MENU'
  | 'LOGGING_OUT'
  | 'LOGGING_OUT_UNREACHABLE'
  | 'CLOSING_APP'
  | 'CLOSING_APP_UNREACHABLE'
  | 'CLOSING_APP_UNAUTHORIZED'
  | 'UPDATING_APP'
  | 'UPDATING_APP_UNREACHABLE'
  | 'UPDATING_APP_UNAUTHORIZED';

export class UnauthorizedError extends Error {
  public constructor() {
    super('Unauthorized');
  }
}

export class ServerUnreachableError extends Error {
  public constructor() {
    super('Server unreachable');
  }
}

export const releaseDocument = async (
  document: LockedDocumentInResponse
): Promise<void> => {
  await unlockDocument(document.documentId, document.casusId);
};

const unlockDocument = async (
  documentId: string,
  casusId: string
): Promise<boolean | undefined> => {
  const response = await handledBackend.unlockDocument({
    parameters: {
      documentId,
      casusId,
    },
  });
  if (!response.hasError) {
    return true;
  }
};

export const getLockedDocuments = (
  propagateError?: boolean
): Promise<LockedDocumentInResponse[]> =>
  new Promise((resolve, reject) =>
    handledBackend.getLockedDocumentsOfLoggedInUser({
      parameters: {},
      onSuccess: ({body}) => {
        resolve(body.lockedDocuments);
      },
      localHandlers: {
        '401': () => {
          if (propagateError) {
            reject(new UnauthorizedError());
          }
        },
      },
      afterErrorHandler: () => {
        if (propagateError) {
          reject(new ServerUnreachableError());
        }
      },
    })
  );

const WATCHED_DOCUMENT_IDS_TIMEOUT_IN_MS = 5000;

/**
 * Resolves to the ids of the documents watched on this workstation, or
 * `undefined` when they cannot be determined (browser, or Electron not
 * answering within the timeout). `undefined` means "unknown" — callers treat
 * all documents as watched so the exit flows never block silently.
 */
export const getWatchedDocumentIds = (): Promise<Set<string> | undefined> =>
  new Promise((resolve) => {
    if (!isElectron()) {
      resolve(undefined);
      return;
    }
    const timeout = setTimeout(
      () => resolve(undefined),
      WATCHED_DOCUMENT_IDS_TIMEOUT_IN_MS
    );
    exchangeElectronMessage(
      {
        payload: null,
        type: BrowserMessageType.GET_WATCHED_DOCUMENT_IDS,
      },
      (watchedIds) => {
        clearTimeout(timeout);
        resolve(new Set(watchedIds));
      }
    );
  });

/**
 * Whether any of the given documents is watched on this workstation. Only
 * watched documents block closing, logging out, or updating — locks from
 * other workstations cannot be influenced by exiting this app.
 */
export const hasWatchedDocuments = (
  documents: LockedDocumentInResponse[],
  watchedDocumentIds: Set<string> | undefined
): boolean =>
  watchedDocumentIds
    ? documents.some(({documentId}) => watchedDocumentIds.has(documentId))
    : documents.length > 0;

export const getLockedDocumentById = async (
  documentId: string,
  casusId: string
): Promise<LockedDocumentInResponse | undefined> => {
  const response = await handledBackend.getLockedDocumentById({
    parameters: {
      casusId,
      documentId,
    },
  });
  if (!response.hasError) {
    return response.body?.body.document;
  }
};
